// =============================================
// Admin Budget Dashboard — לוח בקרה תקציבי
// Network Manager sees budget across all kindergartens
// =============================================

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wallet, TrendingUp, PiggyBank, Building2, Receipt,
  Calendar, Filter, X, ChevronLeft, Loader2,
  ArrowUpRight, Package
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import { adminApi } from '../../services/api';
import { adminColors } from '../../styles/colors';

const colors = {
  ...adminColors,
  green: '#22c55e',
  greenLight: '#dcfce7',
  red: '#ef4444',
  redLight: '#fef2f2',
  orange: '#f59e0b',
  orangeLight: '#fffbeb',
  blue: '#3b82f6',
  blueLight: '#dbeafe',
};

interface KgBudgetSummary {
  kindergartenPublicId: string;
  kindergartenName: string;
  totalBudget: number;
  totalSpent: number;
  remainingBudget: number;
  sourceCount: number;
  expenseCount: number;
}

interface DashboardData {
  totalBudgetAcrossKindergartens: number;
  totalSpentAcrossKindergartens: number;
  kindergartenCount: number;
  kindergartens: KgBudgetSummary[];
}

interface NetworkExpense {
  publicId: string;
  description: string;
  amount: number;
  expenseDate: string;
  categoryName?: string;
  budgetSourceName?: string;
  kindergartenName?: string;
  status: number;
  invoiceOcrStatus?: number;
}

const statusLabels: Record<number, string> = {
  0: 'טיוטה', 1: 'ממתין', 2: 'מאושר', 3: 'נדחה', 4: 'דווח',
};
const statusColors: Record<number, string> = {
  0: colors.textLight, 1: colors.orange, 2: colors.green, 3: colors.red, 4: colors.blue,
};

export default function AdminBudgetDashboard() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [expenses, setExpenses] = useState<NetworkExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Drill-down state
  const [selectedKg, setSelectedKg] = useState<KgBudgetSummary | null>(null);

  // Load dashboard
  useEffect(() => {
    const loadData = async () => {
      if (!orgFilter) { setLoading(false); return; }
      setLoading(true);
      try {
        const result = await adminApi.getBudgetDashboard(orgFilter);
        if (result.success && result.data) {
          setDashboard(result.data);
        }
      } catch (err) {
        console.error('Failed to load budget dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgFilter]);

  // Load expenses for drill-down
  const loadExpenses = async () => {
    if (!orgFilter) return;
    setLoadingExpenses(true);
    try {
      const result = await adminApi.getBudgetExpenses(orgFilter, dateFrom || undefined, dateTo || undefined);
      if (result.success && result.data) {
        setExpenses(result.data);
      }
    } catch (err) {
      console.error('Failed to load expenses:', err);
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleKgClick = async (kg: KgBudgetSummary) => {
    setSelectedKg(kg);
    if (expenses.length === 0) {
      await loadExpenses();
    }
  };

  // Filter expenses for selected KG
  const kgExpenses = selectedKg
    ? expenses.filter(e => e.kindergartenName === selectedKg.kindergartenName)
    : [];

  const fmtMoney = (n: number) => `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const fmtDate = (s: string) => {
    try { return new Date(s).toLocaleDateString('he-IL'); } catch { return s; }
  };

  const totalBudget = dashboard?.totalBudgetAcrossKindergartens || 0;
  const totalSpent = dashboard?.totalSpentAcrossKindergartens || 0;
  const totalRemaining = totalBudget - totalSpent;
  const utilization = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;

  // No org selected
  if (!orgFilter) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Wallet size={24} color={colors.primary} /> לוח בקרה תקציבי
        </h1>
        <div style={{
          textAlign: 'center', padding: '60px 24px', backgroundColor: colors.white,
          borderRadius: '14px', border: `1px solid ${colors.border}`,
        }}>
          <Building2 size={48} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            בחר ארגון
          </h3>
          <p style={{ fontSize: '13px', color: colors.textLight }}>
            בחר ארגון מהתפריט הצדדי כדי לצפות בלוח הבקרה התקציבי
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Wallet size={24} color={colors.primary} /> לוח בקרה תקציבי
      </h1>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          <Loader2 size={24} style={{ margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
          <p style={{ fontSize: '13px' }}>טוען נתוני תקציב...</p>
        </div>
      )}

      {!loading && dashboard && (
        <>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {/* Total Budget */}
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              border: `1px solid ${colors.border}`, padding: '16px', textAlign: 'center',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                backgroundColor: colors.greenLight, display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
              }}>
                <Wallet size={18} color={colors.green} />
              </div>
              <p style={{ fontSize: '11px', color: colors.textLight, margin: '0 0 2px 0' }}>תקציב כולל</p>
              <p style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: 0, direction: 'ltr' }}>{fmtMoney(totalBudget)}</p>
            </div>

            {/* Total Spent */}
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              border: `1px solid ${colors.border}`, padding: '16px', textAlign: 'center',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                backgroundColor: colors.orangeLight, display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
              }}>
                <Receipt size={18} color={colors.orange} />
              </div>
              <p style={{ fontSize: '11px', color: colors.textLight, margin: '0 0 2px 0' }}>הוצאות</p>
              <p style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: 0, direction: 'ltr' }}>{fmtMoney(totalSpent)}</p>
            </div>

            {/* Remaining */}
            <div style={{
              backgroundColor: colors.white, borderRadius: '14px',
              border: `1px solid ${colors.border}`, padding: '16px', textAlign: 'center',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '10px',
                backgroundColor: colors.blueLight, display: 'flex',
                alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px',
              }}>
                <PiggyBank size={18} color={colors.blue} />
              </div>
              <p style={{ fontSize: '11px', color: colors.textLight, margin: '0 0 2px 0' }}>נותר</p>
              <p style={{ fontSize: '18px', fontWeight: '700', color: totalRemaining > 0 ? colors.green : colors.red, margin: 0, direction: 'ltr' }}>{fmtMoney(totalRemaining)}</p>
            </div>
          </div>

          {/* Utilization bar */}
          <div style={{
            backgroundColor: colors.white, borderRadius: '12px',
            border: `1px solid ${colors.border}`, padding: '14px 16px', marginBottom: '20px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>ניצולת תקציב כוללת</span>
              <span style={{ fontSize: '13px', fontWeight: '700', color: utilization > 90 ? colors.red : utilization > 70 ? colors.orange : colors.green }}>
                {utilization}%
              </span>
            </div>
            <div style={{ height: '8px', backgroundColor: colors.background, borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.min(utilization, 100)}%`,
                backgroundColor: utilization > 90 ? colors.red : utilization > 70 ? colors.orange : colors.green,
                borderRadius: '4px',
                transition: 'width 0.5s ease',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
              <span>{dashboard.kindergartenCount} גנים</span>
              <span>{fmtMoney(totalSpent)} מתוך {fmtMoney(totalBudget)}</span>
            </div>
          </div>

          {/* Date filter */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap',
          }}>
            <Calendar size={16} color={colors.textLight} />
            <input
              type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{
                padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                fontSize: '12px', outline: 'none',
              }}
            />
            <span style={{ fontSize: '12px', color: colors.textLight }}>עד</span>
            <input
              type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{
                padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: '8px',
                fontSize: '12px', outline: 'none',
              }}
            />
            <button
              onClick={loadExpenses}
              disabled={loadingExpenses}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: '8px',
                backgroundColor: colors.primary, color: 'white', cursor: 'pointer',
                fontSize: '12px', fontWeight: '600',
              }}
            >
              {loadingExpenses ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <><Filter size={14} /> סנן</>}
            </button>
          </div>

          {/* Kindergarten Budget Grid */}
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            גנים ברשת ({dashboard.kindergartens.length})
          </h3>

          {dashboard.kindergartens.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 24px', backgroundColor: colors.white,
              borderRadius: '14px', border: `1px solid ${colors.border}`,
            }}>
              <Package size={32} color={colors.textLight} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <p style={{ fontSize: '13px', color: colors.textLight }}>אין נתוני תקציב עדיין</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {dashboard.kindergartens.map((kg, index) => {
                const kgUtil = kg.totalBudget > 0 ? Math.round((kg.totalSpent / kg.totalBudget) * 100) : 0;
                const barColor = kgUtil > 90 ? colors.red : kgUtil > 70 ? colors.orange : colors.green;

                return (
                  <motion.div
                    key={kg.kindergartenPublicId}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.04 }}
                    onClick={() => handleKgClick(kg)}
                    style={{
                      backgroundColor: colors.white, borderRadius: '12px',
                      border: `1px solid ${colors.border}`, padding: '16px',
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    whileHover={{ y: -2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <div>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Building2 size={14} color={colors.primary} /> {kg.kindergartenName}
                        </h4>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: colors.textLight }}>
                          <span>תקציב: {fmtMoney(kg.totalBudget)}</span>
                          <span>הוצאות: {fmtMoney(kg.totalSpent)}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: colors.textLight }}>
                        <span style={{ fontSize: '12px' }}>צפה</span>
                        <ArrowUpRight size={14} />
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div style={{ height: '6px', backgroundColor: colors.background, borderRadius: '3px', overflow: 'hidden', marginBottom: '6px' }}>
                      <div style={{
                        height: '100%', width: `${Math.min(kgUtil, 100)}%`,
                        backgroundColor: barColor, borderRadius: '3px', transition: 'width 0.4s',
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: colors.textLight }}>
                      <span>{kgUtil}% ניצולת · {kg.sourceCount} מקורות</span>
                      <span style={{ color: barColor, fontWeight: '600' }}>
                        נותר {fmtMoney(kg.remainingBudget)}
                      </span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Drill-down Modal */}
      <AnimatePresence>
        {selectedKg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '16px',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedKg(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              style={{
                backgroundColor: colors.white, borderRadius: '16px',
                width: '100%', maxWidth: '600px', maxHeight: '80vh',
                padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                overflow: 'auto',
              }}
              onMouseDown={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.text, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Building2 size={16} color={colors.primary} /> {selectedKg.kindergartenName}
                  </h3>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: colors.textLight }}>
                    <span>תקציב: {fmtMoney(selectedKg.totalBudget)}</span>
                    <span>הוצאות: {fmtMoney(selectedKg.totalSpent)}</span>
                    <span style={{ color: selectedKg.remainingBudget > 0 ? colors.green : colors.red, fontWeight: '600' }}>
                      נותר: {fmtMoney(selectedKg.remainingBudget)}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelectedKg(null)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={20} color={colors.textLight} />
                </button>
              </div>

              {/* Expenses list */}
              {loadingExpenses ? (
                <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
                  <Loader2 size={20} style={{ margin: '0 auto 8px', animation: 'spin 1s linear infinite' }} />
                  <p style={{ fontSize: '12px' }}>טוען הוצאות...</p>
                </div>
              ) : kgExpenses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: colors.textLight }}>
                  <Receipt size={24} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                  <p style={{ fontSize: '13px' }}>אין הוצאות רשומות לגן זה</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 80px 90px 70px',
                    gap: '8px', padding: '8px 10px',
                    fontSize: '11px', fontWeight: '600', color: colors.textLight,
                    borderBottom: `1px solid ${colors.border}`,
                  }}>
                    <span>תיאור</span>
                    <span style={{ textAlign: 'center' }}>סכום</span>
                    <span style={{ textAlign: 'center' }}>תאריך</span>
                    <span style={{ textAlign: 'center' }}>סטטוס</span>
                  </div>
                  {kgExpenses.map(exp => (
                    <div
                      key={exp.publicId}
                      style={{
                        display: 'grid', gridTemplateColumns: '1fr 80px 90px 70px',
                        gap: '8px', padding: '8px 10px', fontSize: '12px',
                        borderBottom: `1px solid ${colors.background}`,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <span style={{ color: colors.text, fontWeight: '500', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {exp.description}
                        </span>
                        {exp.categoryName && (
                          <span style={{ fontSize: '10px', color: colors.textLight }}>{exp.categoryName}</span>
                        )}
                      </div>
                      <span style={{ textAlign: 'center', fontWeight: '600', color: colors.text, direction: 'ltr' }}>
                        ₪{exp.amount.toFixed(2)}
                      </span>
                      <span style={{ textAlign: 'center', color: colors.textLight, fontSize: '11px' }}>
                        {fmtDate(exp.expenseDate)}
                      </span>
                      <span style={{
                        textAlign: 'center', fontSize: '10px', fontWeight: '600',
                        color: statusColors[exp.status] || colors.textLight,
                      }}>
                        {statusLabels[exp.status] || '—'}
                      </span>
                    </div>
                  ))}
                  {/* Total */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 80px 90px 70px',
                    gap: '8px', padding: '10px 10px', fontWeight: '700', fontSize: '13px',
                    borderTop: `2px solid ${colors.primary}30`, color: colors.text,
                  }}>
                    <span>סה״כ ({kgExpenses.length} הוצאות)</span>
                    <span style={{ textAlign: 'center', direction: 'ltr', color: colors.primary }}>
                      ₪{kgExpenses.reduce((s, e) => s + e.amount, 0).toFixed(2)}
                    </span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
